import {
  Button,
  Center,
  CoinsIcon,
  Flex,
  FormProvider,
  Image,
  Text,
  TextField,
  useDebounce,
  useForm,
} from '@agrotoken-tech/ui';
import { useTranslation } from '@bcba/react-hooks';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { useQuery } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import { useUserContext } from '../../contexts/UserContext';
import { ExplorerSearchDossierDto } from '../../services/explorer/dtos/SearchDossierDto.dto';
import { getDigitalDossierSearch } from '../../services/explorer/getDigitalDossierSearch';
import SearchArt from './assets/search-art.jpg';
import { ExplorerResultsTable } from './ExplorerResultsTable';

const decorativeImageAltText = '';

const resolver = classValidatorResolver(ExplorerSearchDossierDto);

export const DigitalDossierSearch = () => {
  const formMethods = useForm({ resolver });
  const { t } = useTranslation();
  const { watch, trigger } = formMethods;
  const search = watch('search');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(search, 500);

  // TODO: min-length of 3, when to apply validation ?
  // We have a search button, but it's likely to not be used since we are searching on input change
  // We need to discuss when to run the validations and if to show an error message
  const {
    data,
    isSuccess,
    isLoading: isSearchLoading,
    refetch,
  } = useQuery({
    queryKey: ['explorer', page, debouncedSearch],
    queryFn: () => getDigitalDossierSearch({ search: debouncedSearch, page }),
    enabled: false,
  });

  const { explorerEnableAvailableQueries } = useFlags();

  const {
    user: { availableQueries },
  } = useUserContext();

  useEffect(() => {
    if (debouncedSearch) {
      trigger().then((isValidFormState) => {
        if (isValidFormState) {
          refetch();
        }
      });
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (page !== 1) {
      refetch();
    }
  }, [page]);

  const onSubmit = (data: any) => {
    refetch();
  };

  return (
    <Center flexDir="column">
      <Image src={SearchArt} alt={decorativeImageAltText} h="172px" w="309px" />
      <Flex
        as="form"
        gap="3"
        maxW="529px"
        direction={{ base: 'column', md: 'row' }}
        onSubmit={formMethods.handleSubmit(onSubmit)}
      >
        <FormProvider {...formMethods}>
          <Flex flexDir="column" w="full" mt={{ base: 4, md: 0 }}>
            <TextField
              errorMessageFormatter={t}
              name="search"
              placeholder={t(
                'explorer.digitalDossier.explorer.newSearch.search.placeholder',
              )}
            />
            {explorerEnableAvailableQueries ? (
              <Flex marginTop={2} gap={1} alignItems="center">
                <CoinsIcon h="16px" w="16px" ml="1" />
                <Text color="content-secondary" gap={2}>
                  {t(
                    'explorer.digitalDossier.explorer.newSearch.search.availableInquiries',
                  )}{' '}
                </Text>
                <Text color="content-secondary" variant="bold">
                  {availableQueries}
                </Text>
              </Flex>
            ) : null}
          </Flex>

          <Button
            variant="primary"
            type="submit"
            w={{ base: '100%', md: 'auto' }}
            isLoading={isSearchLoading}
          >
            {t('common.search')}
          </Button>
        </FormProvider>
      </Flex>
      {isSuccess ? (
        <ExplorerResultsTable
          data={data}
          setPage={setPage}
          refetch={() => refetch()}
        />
      ) : null}
    </Center>
  );
};
